import { useEffect, useMemo, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  CreditCard,
  Landmark,
  Plus,
  ReceiptText,
  Search,
  ShoppingBag,
  SlidersHorizontal,
  Trash2,
  WalletCards,
  X,
} from "lucide-react";

import { auth, db } from "../firebase/config";

import {
  excluirDivida,
  marcarParcelaPaga,
  ouvirDividas,
  salvarDivida,
} from "../services/dividasService";

import { formatarMoeda, moedaParaNumero } from "../utils/money";
import "../styles/pages/dividas.css";

function hojeISO() {
  return new Date().toLocaleDateString("en-CA");
}

function formatarData(data) {
  if (!data) return "-";
  if (typeof data === "string" && data.includes("-")) {
    const [ano, mes, dia] = data.slice(0, 10).split("-");
    if (ano && mes && dia) return `${dia}/${mes}/${ano}`;
  }
  return data;
}

function normalizarDia(valor) {
  const numero = Number(valor);
  if (!Number.isFinite(numero)) return 0;
  return Math.min(Math.max(numero, 1), 31);
}

function dataISO(ano, mesZero, dia) {
  const ultimoDiaMes = new Date(ano, mesZero + 1, 0).getDate();
  const diaSeguro = Math.min(normalizarDia(dia), ultimoDiaMes);
  return new Date(ano, mesZero, diaSeguro, 12, 0, 0).toLocaleDateString("en-CA");
}

function adicionarMesesISO(dataISOBase, mesesParaAdicionar) {
  const base = dataISOBase ? new Date(`${dataISOBase}T12:00:00`) : new Date();
  const diaOriginal = base.getDate();
  const ano = base.getFullYear();
  const mes = base.getMonth() + Number(mesesParaAdicionar || 0);
  const ultimoDiaDestino = new Date(ano, mes + 1, 0).getDate();
  const diaSeguro = Math.min(diaOriginal, ultimoDiaDestino);
  return new Date(ano, mes, diaSeguro, 12, 0, 0).toLocaleDateString("en-CA");
}

function proximaDataDoDia(diaInformado) {
  const dia = normalizarDia(diaInformado);
  if (!dia) return "";

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  let ano = hoje.getFullYear();
  let mes = hoje.getMonth();
  let data = new Date(`${dataISO(ano, mes, dia)}T12:00:00`);

  if (data < hoje) {
    mes += 1;
    if (mes > 11) {
      mes = 0;
      ano += 1;
    }
    data = new Date(`${dataISO(ano, mes, dia)}T12:00:00`);
  }

  return data.toLocaleDateString("en-CA");
}

function formatarDiaRecorrente(valor) {
  const dia = normalizarDia(valor);
  return dia > 0 ? `Todo dia ${String(dia).padStart(2, "0")}` : "-";
}

function calcularFaturaDaCompra(compra, cartao) {
  const dataCompra = compra?.data ? new Date(`${compra.data}T12:00:00`) : new Date();
  let ano = dataCompra.getFullYear();
  let mes = dataCompra.getMonth();
  const diaCompra = dataCompra.getDate();
  const fechamentoDia = normalizarDia(cartao.fechamentoDia);
  const vencimentoDia = normalizarDia(cartao.vencimentoDia);

  if (fechamentoDia && diaCompra > fechamentoDia) {
    mes += 1;
    if (mes > 11) {
      mes = 0;
      ano += 1;
    }
  }

  let mesVencimento = mes;
  let anoVencimento = ano;
  if (vencimentoDia <= fechamentoDia) {
    mesVencimento += 1;
    if (mesVencimento > 11) {
      mesVencimento = 0;
      anoVencimento += 1;
    }
  }

  return {
    chave: `${ano}-${String(mes + 1).padStart(2, "0")}`,
    fechamento: dataISO(ano, mes, fechamentoDia),
    vencimento: dataISO(anoVencimento, mesVencimento, vencimentoDia),
  };
}

function chaveMesAtual() {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;
}

function diasAtraso(vencimento) {
  if (!vencimento) return 0;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const venc = new Date(`${vencimento}T12:00:00`);
  venc.setHours(0, 0, 0, 0);
  const diff = hoje.getTime() - venc.getTime();
  return Math.max(Math.floor(diff / (1000 * 60 * 60 * 24)), 0);
}

function tipoVisual(divida) {
  const texto = `${divida?.nome || ""} ${divida?.categoria || ""}`.toLowerCase();
  if (texto.includes("banco") || texto.includes("emprest") || texto.includes("financi")) return "banco";
  if (texto.includes("loja") || texto.includes("compra") || texto.includes("magazine")) return "loja";
  return "cartao";
}

function DebtIcon({ tipo }) {
  if (tipo === "banco") return <Landmark size={22} />;
  if (tipo === "loja") return <ShoppingBag size={22} />;
  return <CreditCard size={22} />;
}

function obterLogoBanco(nome = "") {
  const texto = String(nome).toLowerCase();
  if (texto.includes("nubank") || texto.includes("nu ") || texto === "nu") return "nu";
  if (texto.includes("mercado")) return "MP";
  if (texto.includes("inter")) return "Inter";
  if (texto.includes("itau") || texto.includes("itaú")) return "Itaú";
  if (texto.includes("bradesco")) return "Bra";
  if (texto.includes("santander")) return "San";
  if (texto.includes("caixa")) return "CEF";
  if (texto.includes("bb") || texto.includes("brasil")) return "BB";
  if (texto.includes("c6")) return "C6";
  if (texto.includes("picpay")) return "Pic";
  return (String(nome).trim().split(" ")[0] || "Card").slice(0, 4);
}

function classeLogoBanco(nome = "") {
  const texto = String(nome).toLowerCase();
  if (texto.includes("nubank") || texto.includes("nu ") || texto === "nu") return "nubank";
  if (texto.includes("mercado")) return "mercado";
  if (texto.includes("inter")) return "inter";
  if (texto.includes("itau") || texto.includes("itaú")) return "itau";
  if (texto.includes("bradesco")) return "bradesco";
  if (texto.includes("santander")) return "santander";
  if (texto.includes("caixa")) return "caixa";
  if (texto.includes("bb") || texto.includes("brasil")) return "bb";
  if (texto.includes("c6")) return "c6";
  if (texto.includes("picpay")) return "picpay";
  return "default";
}

export default function DividasPage({ uid }) {
  const [dividas, setDividas] = useState([]);
  const [modalAberto, setModalAberto] = useState(false);
  const [modalCartaoAberto, setModalCartaoAberto] = useState(false);
  const [modalCompraAberto, setModalCompraAberto] = useState(false);
  const [cartaoCompraAtual, setCartaoCompraAtual] = useState(null);
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState("todas");
  const [erro, setErro] = useState("");
  const [erroCartao, setErroCartao] = useState("");
  const [erroCompra, setErroCompra] = useState("");
  const [salvando, setSalvando] = useState(false);

  const [nome, setNome] = useState("");
  const [categoria, setCategoria] = useState("Cartão");
  const [valorTotal, setValorTotal] = useState("");
  const [valorPago, setValorPago] = useState("");
  const [parcelas, setParcelas] = useState("1");
  const [parcelaAtual, setParcelaAtual] = useState("0");
  const [valorParcela, setValorParcela] = useState("");
  const [vencimento, setVencimento] = useState(hojeISO());
  const [observacao, setObservacao] = useState("");

  const [cartoes, setCartoes] = useState([]);
  const [comprasCartao, setComprasCartao] = useState([]);
  const [nomeCartao, setNomeCartao] = useState("");
  const [limiteCartao, setLimiteCartao] = useState("");
  const [fechamentoCartao, setFechamentoCartao] = useState("");
  const [vencimentoCartao, setVencimentoCartao] = useState("");

  const [descricaoCompra, setDescricaoCompra] = useState("");
  const [valorCompra, setValorCompra] = useState("");
  const [dataCompra, setDataCompra] = useState(hojeISO());
  const [categoriaCompra, setCategoriaCompra] = useState("Compra");
  const [parcelasCompra, setParcelasCompra] = useState("1");
  const [cartoesMesExpandido, setCartoesMesExpandido] = useState({});
  const [cartaoParcelasAberto, setCartaoParcelasAberto] = useState(null);

  useEffect(() => {
    if (!uid) return;
    const unsub = ouvirDividas(uid, setDividas);
    return () => unsub();
  }, [uid]);

  useEffect(() => {
    if (!uid) return;

    const ref = collection(db, "usuarios", uid, "cartoes");
    const unsub = onSnapshot(
      ref,
      (snapshot) => {
        setCartoes(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      },
      (error) => {
        console.error("Erro ao carregar cartões", error);
        setCartoes([]);
      }
    );

    return () => unsub();
  }, [uid]);

  useEffect(() => {
    if (!uid) return;

    const ref = collection(db, "usuarios", uid, "comprasCartao");
    const unsub = onSnapshot(
      ref,
      (snapshot) => {
        setComprasCartao(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      },
      (error) => {
        console.error("Erro ao carregar compras do cartão", error);
        setComprasCartao([]);
      }
    );

    return () => unsub();
  }, [uid]);

  const cartoesComFatura = useMemo(() => {
    return cartoes.map((cartao) => {
      const compras = comprasCartao
        .filter((compra) => compra.cartaoId === cartao.id)
        .map((compra) => ({ ...compra, fatura: calcularFaturaDaCompra(compra, cartao) }))
        .sort((a, b) => String(b.data).localeCompare(String(a.data)));

      // Compra parcelada consome o limite total enquanto as parcelas ainda não foram pagas.
      // A fatura atual é calculada automaticamente pela data de fechamento do cartão.
      // Exemplo: fecha dia 05; compra feita dia 06 entra na próxima fatura.
      const comprasAbertas = compras.filter((compra) => compra.status !== "paga");
      const proximoFechamento = proximaDataDoDia(cartao.fechamentoDia);
      const proximoVencimento = proximaDataDoDia(cartao.vencimentoDia);

      // A fatura atual não é o mês do calendário. Ela é a próxima fatura aberta do cartão.
      // Exemplo: cartão fecha dia 05; compra feita dia 30/05 entra na fatura de 05/06.
      const chaveFaturaAtual = proximoFechamento ? proximoFechamento.slice(0, 7) : chaveMesAtual();
      const comprasDoMes = comprasAbertas.filter((compra) => {
        const chaveCompra = compra.fatura?.chave || compra.faturaChave;
        return chaveCompra === chaveFaturaAtual;
      });
      const faturaAtual = comprasDoMes.reduce((acc, compra) => acc + Number(compra.valor || 0), 0);
      const limite = Number(cartao.limite || 0);

      // Limite usado precisa considerar TODAS as parcelas abertas da compra,
      // não apenas a parcela que entrou na fatura atual.
      // Exemplo: compra de R$ 300 em 2x usa R$ 300 do limite,
      // mas a fatura atual mostra apenas R$ 150.
      const limiteUsado = comprasAbertas.reduce((acc, compra) => acc + Number(compra.valor || 0), 0);
      const limiteDisponivel = Math.max(limite - limiteUsado, 0);
      const usoPercentual = limite > 0 ? Math.min((limiteUsado / limite) * 100, 100) : 0;

      return {
        ...cartao,
        compras,
        comprasAbertas,
        comprasDoMes,
        faturaAtual,
        limite,
        limiteUsado,
        limiteDisponivel,
        usoPercentual,
        proximoFechamento,
        proximoVencimento,
      };
    });
  }, [cartoes, comprasCartao]);

  const resumo = useMemo(() => {
    const abertas = dividas.filter((item) => item.status !== "quitada");

    // Cartões ficam separados da lista de dívidas, mas entram nos cards de resumo.
    // Total em dívidas = todas as parcelas abertas dos cartões + dívidas normais.
    // A pagar este mês = somente a fatura atual do cartão + parcelas normais do mês.
    const totalParcelasAbertasCartoes = cartoesComFatura.reduce((acc, cartao) => {
      return acc + cartao.comprasAbertas.reduce((soma, compra) => soma + Number(compra.valor || 0), 0);
    }, 0);

    const totalFaturasAbertasCartoes = cartoesComFatura.reduce((acc, cartao) => {
      return acc + Number(cartao.faturaAtual || 0);
    }, 0);

    const totalDividas = abertas.reduce((acc, item) => acc + Number(item.valorRestante || 0), 0);
    const previstoDividasMes = abertas.reduce((acc, item) => acc + Number(item.valorParcela || 0), 0);

    const total = totalDividas + totalParcelasAbertasCartoes;
    const previstoMes = previstoDividasMes + totalFaturasAbertasCartoes;

    const quitadas = dividas
      .filter((item) => item.status === "quitada")
      .reduce((acc, item) => acc + Number(item.valorPago || item.valorTotal || 0), 0);

    const atrasadas = abertas
      .filter((item) => diasAtraso(item.vencimento) > 0)
      .reduce((acc, item) => acc + Number(item.valorParcela || 0), 0);

    const pagoMes = dividas.reduce((acc, item) => acc + Number(item.valorPago || 0), 0);

    return { total, previstoMes, quitadas, atrasadas, pagoMes, totalFaturasAbertasCartoes, totalParcelasAbertasCartoes };
  }, [dividas, cartoesComFatura]);

  const graficoDividas = useMemo(() => {
    const itens = [
      { label: "Total", value: resumo.total, className: "total" },
      { label: "Mês", value: resumo.previstoMes, className: "month" },
      { label: "Pago", value: resumo.pagoMes, className: "paid" },
      { label: "Atraso", value: resumo.atrasadas, className: "late" },
    ];
    const maiorValor = Math.max(...itens.map((item) => Number(item.value || 0)), 1);
    return itens.map((item) => ({ ...item, value: Number(item.value || 0), percent: Math.max((Number(item.value || 0) / maiorValor) * 100, 4) }));
  }, [resumo]);

  const porcentagemPago = resumo.previstoMes > 0 ? Math.min((resumo.pagoMes / resumo.previstoMes) * 100, 100) : 0;

  const dividasFiltradas = useMemo(() => {
    return dividas.filter((item) => {
      const texto = `${item.nome || ""} ${item.categoria || ""} ${item.observacao || ""}`.toLowerCase();
      const bateBusca = texto.includes(busca.trim().toLowerCase());
      if (!bateBusca) return false;
      if (filtro === "quitadas") return item.status === "quitada";
      if (filtro === "abertas") return item.status !== "quitada";
      if (filtro === "atrasadas") return item.status !== "quitada" && diasAtraso(item.vencimento) > 0;
      return true;
    });
  }, [dividas, busca, filtro]);

  async function cadastrarDivida(e) {
    e.preventDefault();
    setErro("");

    if (!nome.trim()) {
      setErro("Informe o nome da dívida.");
      return;
    }

    const total = moedaParaNumero(valorTotal);
    const pago = moedaParaNumero(valorPago);
    const qtdParcelas = Number(parcelas || 1);
    const valorParcelaNumero = moedaParaNumero(valorParcela);

    if (total <= 0) {
      setErro("Informe o valor total da dívida.");
      return;
    }

    try {
      setSalvando(true);
      await salvarDivida(uid, {
        nome,
        categoria,
        valorTotal: total,
        valorPago: pago,
        parcelas: qtdParcelas,
        parcelaAtual: Number(parcelaAtual || 0),
        valorParcela: valorParcelaNumero > 0 ? valorParcelaNumero : total / qtdParcelas,
        vencimento,
        observacao,
      });

      setNome("");
      setCategoria("Cartão");
      setValorTotal("");
      setValorPago("");
      setParcelas("1");
      setParcelaAtual("0");
      setValorParcela("");
      setVencimento(hojeISO());
      setObservacao("");
      setModalAberto(false);
    } catch (error) {
      console.error(error);
      setErro(error.message || "Erro ao salvar dívida.");
    } finally {
      setSalvando(false);
    }
  }

  async function cadastrarCartao(e) {
    e.preventDefault();
    setErroCartao("");

    const uidAutenticado = auth.currentUser?.uid;
    const uidParaSalvar = uid || uidAutenticado;

    console.log("[Invexa] UID recebido por props:", uid);
    console.log("[Invexa] UID autenticado:", uidAutenticado);
    console.log("[Invexa] UID usado para salvar cartão:", uidParaSalvar);

    if (!uidParaSalvar) {
      setErroCartao("Não foi possível identificar seu usuário. Saia e entre novamente no app.");
      return;
    }

    if (!nomeCartao.trim()) {
      setErroCartao("Informe o nome do cartão.");
      return;
    }

    const limite = moedaParaNumero(limiteCartao);
    if (limite <= 0) {
      setErroCartao("Informe o limite total do cartão.");
      return;
    }

    const fechamentoDia = normalizarDia(fechamentoCartao);
    const vencimentoDia = normalizarDia(vencimentoCartao);
    if (!fechamentoDia) {
      setErroCartao("Informe o dia de fechamento da fatura.");
      return;
    }
    if (!vencimentoDia) {
      setErroCartao("Informe o dia de vencimento da fatura.");
      return;
    }

    try {
      setSalvando(true);

      const cartaoRef = doc(collection(db, "usuarios", uidParaSalvar, "cartoes"));
      const dadosCartao = {
        nome: nomeCartao.trim(),
        limite,
        fechamentoDia,
        vencimentoDia,
        criadoEm: serverTimestamp(),
        atualizadoEm: serverTimestamp(),
      };

      console.log("[Invexa] Salvando cartão no Firebase:", {
        caminho: `usuarios/${uidParaSalvar}/cartoes/${cartaoRef.id}`,
        dadosCartao,
      });

      await setDoc(cartaoRef, dadosCartao);

      console.log("[Invexa] Cartão salvo com sucesso no Firebase:", cartaoRef.id);

      setNomeCartao("");
      setLimiteCartao("");
      setFechamentoCartao("");
      setVencimentoCartao("");
      setModalCartaoAberto(false);
    } catch (error) {
      console.error("[Invexa] Erro ao salvar cartão no Firebase:", error);

      if (error?.code === "permission-denied") {
        setErroCartao("O Firebase bloqueou o salvamento. Verifique se sua assinatura Plus/trial está ativa no documento private/billing.");
        return;
      }

      setErroCartao(error?.message || "Erro ao salvar cartão no Firebase.");
    } finally {
      setSalvando(false);
    }
  }

  async function excluirCartao(id) {
    if (!uid || !id) return;

    try {
      const batch = writeBatch(db);
      batch.delete(doc(db, "usuarios", uid, "cartoes", id));

      const comprasRef = collection(db, "usuarios", uid, "comprasCartao");
      const comprasSnap = await getDocs(query(comprasRef, where("cartaoId", "==", id)));
      comprasSnap.forEach((compraDoc) => batch.delete(compraDoc.ref));

      await batch.commit();
    } catch (error) {
      console.error(error);
      alert(error.message || "Erro ao excluir cartão.");
    }
  }

  function abrirCompraCartao(cartao) {
    setCartaoCompraAtual(cartao);
    setDescricaoCompra("");
    setValorCompra("");
    setDataCompra(hojeISO());
    setCategoriaCompra("Compra");
    setParcelasCompra("1");
    setErroCompra("");
    setModalCompraAberto(true);
  }

  async function cadastrarCompraCartao(e) {
    e.preventDefault();
    setErroCompra("");

    if (!cartaoCompraAtual) return;
    if (!descricaoCompra.trim()) {
      setErroCompra("Informe a descrição da compra.");
      return;
    }

    const valor = moedaParaNumero(valorCompra);
    if (valor <= 0) {
      setErroCompra("Informe o valor da compra.");
      return;
    }

    const totalParcelas = Math.max(Number(parcelasCompra || 1) || 1, 1);
    const valorDaParcela = Number((valor / totalParcelas).toFixed(2));
    const diferencaCentavos = Number((valor - valorDaParcela * totalParcelas).toFixed(2));
    const dataBase = dataCompra || hojeISO();
    const compraGrupoId = crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}`;

    const parcelasGeradas = Array.from({ length: totalParcelas }, (_, index) => {
      const dataParcela = adicionarMesesISO(dataBase, index);
      const fatura = calcularFaturaDaCompra({ data: dataParcela }, cartaoCompraAtual);
      const valorFinal = index === totalParcelas - 1
        ? Number((valorDaParcela + diferencaCentavos).toFixed(2))
        : valorDaParcela;

      return {
        id: crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${index}`,
        compraGrupoId,
        cartaoId: cartaoCompraAtual.id,
        descricao: descricaoCompra.trim(),
        valor: valorFinal,
        valorTotalCompra: valor,
        data: dataParcela,
        dataCompraOriginal: dataBase,
        categoria: categoriaCompra || "Compra",
        parcelaNumero: index + 1,
        parcelas: totalParcelas,
        status: "aberta",
        faturaChave: fatura.chave,
        fechamento: fatura.fechamento,
        vencimento: fatura.vencimento,
        criadoEm: new Date().toISOString(),
      };
    });

    try {
      setSalvando(true);

      const batch = writeBatch(db);
      parcelasGeradas.forEach((parcela) => {
        const compraRef = doc(collection(db, "usuarios", uid, "comprasCartao"));
        const { id, ...dadosParcela } = parcela;
        batch.set(compraRef, {
          ...dadosParcela,
          criadoEm: serverTimestamp(),
        });
      });

      await batch.commit();

      setModalCompraAberto(false);
      setCartaoCompraAtual(null);
      setDescricaoCompra("");
      setValorCompra("");
      setDataCompra(hojeISO());
      setCategoriaCompra("Compra");
      setParcelasCompra("1");
    } catch (error) {
      console.error(error);
      setErroCompra(error.message || "Erro ao salvar compra.");
    } finally {
      setSalvando(false);
    }
  }

  async function excluirCompraCartao(id) {
    if (!uid || !id) return;

    try {
      await deleteDoc(doc(db, "usuarios", uid, "comprasCartao", id));
    } catch (error) {
      console.error(error);
      alert(error.message || "Erro ao excluir compra.");
    }
  }



  return (
    <section className="debt-premium-page">
      <div className="debt-page-heading">
        <div>
          <p className="debt-user">Gustavo</p>
          <h2>Dívidas</h2>
          <span>Controle parcelas, cartões, faturas e pagamentos.</span>
        </div>

        <button className="debt-primary-action" type="button" onClick={() => setModalAberto(true)}>
          <Plus size={20} />
          Nova dívida
        </button>
      </div>

      <div className="debt-summary-grid">
        <DebtSummaryCard color="green" title="Total em dívidas" value={formatarMoeda(resumo.total)} caption="Valor total restante" icon={<CreditCard size={25} />} />
        <DebtSummaryCard color="blue" title="A pagar este mês" value={formatarMoeda(resumo.previstoMes)} caption="Total de parcelas" icon={<CalendarDays size={25} />} />
        <DebtSummaryCard color="red" title="Atrasadas" value={formatarMoeda(resumo.atrasadas)} caption="Total em atraso" icon={<AlertTriangle size={25} />} />
        <DebtSummaryCard color="green" title="Dívidas quitadas" value={formatarMoeda(resumo.quitadas)} caption="Valor pago" icon={<CheckCircle2 size={25} />} />
      </div>

      <section className="debt-chart-panel">
        <div className="debt-chart-title"><h3>Gastos com dívidas</h3></div>
        <div className="debt-chart-content">
          <div className="debt-ring-wrap">
            <div className="debt-ring" style={{ "--paid-percent": `${porcentagemPago}%` }}>
              <div><strong>{formatarMoeda(resumo.previstoMes)}</strong><span>Este mês</span></div>
            </div>
          </div>
          <div className="debt-chart-info">
            <div className="debt-info-row"><div className="debt-mini-icon purple"><WalletCards size={21} /></div><div><span>Total previsto (mês)</span><strong>{formatarMoeda(resumo.previstoMes)}</strong></div></div>
            <div className="debt-info-row"><div className="debt-mini-icon green"><CreditCard size={21} /></div><div><span>Total pago</span><strong>{formatarMoeda(resumo.pagoMes)}</strong></div></div>
          </div>
          <div className="debt-month-chart">
            <div className="debt-month-chart-head"><div><span>Resumo visual</span><strong>Valores das dívidas</strong></div><small>Atualizado pelos cadastros</small></div>
            <div className="debt-bars">
              {graficoDividas.map((item) => (
                <div className="debt-bar-row" key={item.label}><span>{item.label}</span><div><i className={item.className} style={{ width: `${item.percent}%` }} /></div><strong>{formatarMoeda(item.value)}</strong></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="debt-cards-panel debt-cards-panel-v2">
        <div className="debt-cards-header">
          <div>
            <span className="debt-section-eyebrow">Cartões com fatura real</span>
            <h3>Meus cartões</h3>
          </div>
          <button className="debt-card-action" type="button" onClick={() => setModalCartaoAberto(true)}><Plus size={18} /> Novo cartão</button>
        </div>

        {cartoesComFatura.length === 0 ? (
          <button className="debt-card-empty debt-card-empty-v2" type="button" onClick={() => setModalCartaoAberto(true)}><CreditCard size={24} /><strong>Nenhum cartão cadastrado</strong><span>Adicionar cartão</span></button>
        ) : (
          <div className="debt-card-list debt-card-list-v2">
            {cartoesComFatura.map((cartao) => (
              <article className="debt-credit-card debt-credit-card-v2 debt-credit-card-showcase" key={cartao.id}>
                <div className="debt-card-main-layout">
                  <div className={`debt-virtual-card ${classeLogoBanco(cartao.nome)}`}>
                    <div className="debt-virtual-top">
                      <div className={`debt-card-logo ${classeLogoBanco(cartao.nome)}`}>{obterLogoBanco(cartao.nome)}</div>
                      <div>
                        <strong>{cartao.nome}</strong>
                        <span>{formatarDiaRecorrente(cartao.fechamentoDia)} fecha • vence em {formatarData(cartao.proximoVencimento)}</span>
                      </div>
                    </div>

                    <div className="debt-virtual-metrics">
                      <div><span>Limite total</span><strong>{formatarMoeda(cartao.limite)}</strong></div>
                      <div><span>Limite usado</span><strong>{formatarMoeda(cartao.limiteUsado)}</strong></div>
                      <div><span>Disponível</span><strong>{formatarMoeda(cartao.limiteDisponivel)}</strong></div>
                      <div><span>Fatura atual</span><strong>{formatarMoeda(cartao.faturaAtual)}</strong></div>
                    </div>

                    <div className="debt-card-usage debt-virtual-usage">
                      <div><span>Uso do limite</span><strong>{cartao.usoPercentual.toFixed(1)}%</strong></div>
                      <div className="debt-card-bar"><span style={{ width: `${cartao.usoPercentual}%` }} /></div>
                    </div>
                  </div>

                  <div className="debt-card-side-panel">
                    <div className="debt-card-side-actions">
                      <button className="debt-card-buy-btn" type="button" onClick={() => abrirCompraCartao(cartao)}><Plus size={15} /> Adicionar compra</button>
                      <button type="button" onClick={() => excluirCartao(cartao.id)} aria-label="Excluir cartão"><Trash2 size={16} /></button>
                    </div>

                    <div className="debt-card-side-grid">
                      <div className="debt-card-date-item"><CalendarDays size={18} /><div><span>Fechamento</span><strong>{formatarData(cartao.proximoFechamento)}</strong></div></div>
                      <div className="debt-card-date-item"><CalendarDays size={18} /><div><span>Vencimento</span><strong>{formatarData(cartao.proximoVencimento)}</strong></div></div>
                    </div>

                    <div className="debt-usage-donut" style={{ "--usage": `${cartao.usoPercentual}%` }}>
                      <div><strong>{cartao.usoPercentual.toFixed(1)}%</strong><span>do limite usado</span></div>
                    </div>

                    <div className="debt-close-invoice-btn debt-auto-invoice-badge" title="A fatura é calculada automaticamente pela data de fechamento."><ReceiptText size={15} /> Fatura automática</div>
                  </div>
                </div>

                <div className="debt-card-purchases">
                  <div className="debt-card-purchases-head">
                    <div>
                      <strong>Compras do mês</strong>
                      <small>Mostrando as últimas compras da fatura atual</small>
                    </div>
                    <div className="debt-purchase-head-actions">
                      {cartao.comprasDoMes.length > 3 && (
                        <button type="button" onClick={() => setCartoesMesExpandido((atuais) => ({ ...atuais, [cartao.id]: !atuais[cartao.id] }))}>
                          {cartoesMesExpandido[cartao.id] ? "Ver menos" : "Ver mais"}
                        </button>
                      )}
                      {cartao.comprasAbertas.length > 0 && (
                        <button type="button" onClick={() => setCartaoParcelasAberto(cartao)}>Ver todas as parcelas</button>
                      )}
                    </div>
                  </div>
                  {cartao.comprasDoMes.length === 0 ? (
                    <p>Nenhuma compra lançada para este mês.</p>
                  ) : (
                    cartao.comprasDoMes
                      .slice(0, cartoesMesExpandido[cartao.id] ? cartao.comprasDoMes.length : 3)
                      .map((compra) => (
                      <div className="debt-purchase-row debt-purchase-row-v2" key={compra.id}>
                        <div className="debt-purchase-icon"><ReceiptText size={16} /></div>
                        <div className="debt-purchase-main">
                          <div className="debt-purchase-title-line">
                            <strong>{compra.descricao}</strong>
                            <span className="debt-purchase-badge">{compra.categoria || "Compra"}</span>
                          </div>
                          <span>
                            Compra: {formatarData(compra.dataCompraOriginal || compra.data)} • Fatura: {formatarData(compra.fechamento)} • Vence: {formatarData(compra.vencimento)}
                          </span>
                          {Number(compra.parcelas || 1) > 1 && (
                            <small>Parcela {compra.parcelaNumero} de {compra.parcelas}</small>
                          )}
                        </div>
                        <div className="debt-purchase-value">
                          <strong>{formatarMoeda(compra.valor)}</strong>
                          {Number(compra.parcelas || 1) > 1 && <span>Total: {formatarMoeda(compra.valorTotalCompra || 0)}</span>}
                        </div>
                        <button type="button" onClick={() => excluirCompraCartao(compra.id)} aria-label="Excluir compra"><Trash2 size={14} /></button>
                      </div>
                    ))
                  )}


                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="debt-list-panel">
        <div className="debt-list-header">
          <div className="debt-list-title"><h3>Minhas dívidas</h3><span>{dividasFiltradas.length} dívida(s)</span></div>
          <div className="debt-list-tools">
            <label className="debt-filter-select"><SlidersHorizontal size={16} /><select value={filtro} onChange={(e) => setFiltro(e.target.value)}><option value="todas">Todas</option><option value="abertas">Abertas</option><option value="atrasadas">Atrasadas</option><option value="quitadas">Quitadas</option></select></label>
            <label><input placeholder="Buscar dívida..." value={busca} onChange={(e) => setBusca(e.target.value)} /><Search size={18} /></label>
          </div>
        </div>

        <div className="debt-table">
          {dividasFiltradas.length === 0 ? (
            <button className="debt-add-placeholder" type="button" onClick={() => setModalAberto(true)}><Plus size={22} />Clique em “Nova dívida” para cadastrar uma dívida</button>
          ) : (
            dividasFiltradas.map((item) => {
              const tipo = tipoVisual(item);
              const total = Number(item.valorTotal || 0);
              const pago = Math.max(Number(item.valorPago || 0), 0);
              const progresso = total > 0 ? Math.min((pago / total) * 100, 100) : 0;
              const estaQuitada = item.status === "quitada";
              const atraso = diasAtraso(item.vencimento);
              return (
                <article className={estaQuitada ? "debt-row debt-row-done" : "debt-row"} key={item.id}>
                  <div className={`debt-row-icon ${tipo}`}><DebtIcon tipo={tipo} /></div>
                  <div className="debt-main"><strong>{item.nome}</strong><span>{item.categoria || "Dívida"}</span></div>
                  <div className="debt-date"><CalendarDays size={17} /><div><span>Vencimento</span><strong>{formatarData(item.vencimento)}</strong>{atraso > 0 && !estaQuitada && <small>{atraso} dia(s) atrasada</small>}</div></div>
                  <div className="debt-parcel"><strong>Parcela {Number(item.parcelaAtual || 0)} de {Number(item.parcelas || 1)}</strong><span>{formatarMoeda(item.valorParcela || 0)} por parcela</span></div>
                  <div className="debt-progress"><div><span style={{ width: `${progresso}%` }} /></div></div>
                  <div className="debt-values"><strong>{formatarMoeda(item.valorRestante || 0)} restante</strong><span>Total: {formatarMoeda(item.valorTotal || 0)}</span></div>
                  <div className="debt-actions"><button className="debt-pay-btn" type="button" disabled={estaQuitada} onClick={() => marcarParcelaPaga(uid, item)}>{estaQuitada ? "Quitada" : "Pagar parcela"}</button><button className="debt-more-btn debt-delete-btn" type="button" aria-label="Excluir dívida" onClick={() => excluirDivida(uid, item.id)}><Trash2 size={17} /></button></div>
                </article>
              );
            })
          )}
        </div>
      </section>

      {modalAberto && (
        <div className="debt-modal-backdrop">
          <section className="debt-modal-card">
            <div className="debt-modal-header"><div><h2>Nova dívida</h2><p>Cadastre parcelas, vencimento e valor inicial pago.</p></div><button type="button" onClick={() => setModalAberto(false)}><X size={20} /></button></div>
            <form className="debt-form" onSubmit={cadastrarDivida}>
              <label>Nome da dívida<input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex: Empréstimo" /></label>
              <div className="debt-form-grid"><label>Categoria<select value={categoria} onChange={(e) => setCategoria(e.target.value)}><option value="Cartão">Cartão</option><option value="Empréstimo">Empréstimo</option><option value="Financiamento">Financiamento</option><option value="Compra parcelada">Compra parcelada</option><option value="Boleto">Boleto</option><option value="Outros">Outros</option></select></label><label>Vencimento<input type="date" value={vencimento} onChange={(e) => setVencimento(e.target.value)} /></label></div>
              <div className="debt-form-grid"><label>Valor total<input value={valorTotal} onChange={(e) => setValorTotal(e.target.value)} placeholder="30,99" inputMode="decimal" /></label><label>Valor já pago<input value={valorPago} onChange={(e) => setValorPago(e.target.value)} placeholder="0,00" inputMode="decimal" /></label></div>
              <div className="debt-form-grid"><label>Parcelas<input value={parcelas} onChange={(e) => setParcelas(e.target.value)} placeholder="12" inputMode="numeric" /></label><label>Parcela atual<input value={parcelaAtual} onChange={(e) => setParcelaAtual(e.target.value)} placeholder="0" inputMode="numeric" /></label></div>
              <label>Valor da parcela<input value={valorParcela} onChange={(e) => setValorParcela(e.target.value)} placeholder="30,99" inputMode="decimal" /></label>
              <label>Observação<textarea value={observacao} onChange={(e) => setObservacao(e.target.value)} placeholder="Opcional" /></label>
              {erro && <div className="debt-modal-error">{erro}</div>}
              <button className="debt-submit-btn" type="submit" disabled={salvando}>{salvando ? "Salvando..." : "Salvar dívida"}</button>
            </form>
          </section>
        </div>
      )}

      {modalCartaoAberto && (
        <div className="debt-modal-backdrop">
          <section className="debt-modal-card">
            <div className="debt-modal-header"><div><h2>Novo cartão</h2></div><button type="button" onClick={() => setModalCartaoAberto(false)}><X size={20} /></button></div>
            <form className="debt-form" onSubmit={cadastrarCartao}>
              <label>Nome do cartão<input value={nomeCartao} onChange={(e) => setNomeCartao(e.target.value)} placeholder="Ex: Nubank" /></label>
              <label>Limite total<input value={limiteCartao} onChange={(e) => setLimiteCartao(e.target.value)} placeholder="1000,00" inputMode="decimal" /></label>
              <div className="debt-form-grid"><label>Dia do fechamento<input type="number" min="1" max="31" value={fechamentoCartao} onChange={(e) => setFechamentoCartao(e.target.value)} placeholder="Ex: 5" inputMode="numeric" /></label><label>Dia do vencimento<input type="number" min="1" max="31" value={vencimentoCartao} onChange={(e) => setVencimentoCartao(e.target.value)} placeholder="Ex: 12" inputMode="numeric" /></label></div>
              {erroCartao && <div className="debt-modal-error">{erroCartao}</div>}
              <button className="debt-submit-btn" type="submit">Salvar cartão</button>
            </form>
          </section>
        </div>
      )}

      {cartaoParcelasAberto && (
        <div className="debt-modal-backdrop">
          <section className="debt-modal-card debt-installments-modal">
            <div className="debt-modal-header">
              <div>
                <h2>Todas as parcelas</h2>
                <p>Cartão: {cartaoParcelasAberto.nome}</p>
              </div>
              <button type="button" onClick={() => setCartaoParcelasAberto(null)}><X size={20} /></button>
            </div>

            <div className="debt-all-installments-list">
              {cartaoParcelasAberto.comprasAbertas.length === 0 ? (
                <p>Nenhuma parcela encontrada.</p>
              ) : (
                cartaoParcelasAberto.comprasAbertas.map((compra) => (
                  <div className="debt-purchase-row debt-purchase-row-v2" key={compra.id}>
                    <div className="debt-purchase-icon"><ReceiptText size={16} /></div>
                    <div className="debt-purchase-main">
                      <div className="debt-purchase-title-line">
                        <strong>{compra.descricao}</strong>
                        <span className="debt-purchase-badge">{compra.fatura?.chave || "Fatura"}</span>
                      </div>
                      <span>Fatura: {formatarData(compra.fechamento)} • Vence: {formatarData(compra.vencimento)}</span>
                      {Number(compra.parcelas || 1) > 1 && <small>Parcela {compra.parcelaNumero} de {compra.parcelas}</small>}
                    </div>
                    <div className="debt-purchase-value">
                      <strong>{formatarMoeda(compra.valor)}</strong>
                      {Number(compra.parcelas || 1) > 1 && <span>Total: {formatarMoeda(compra.valorTotalCompra || 0)}</span>}
                    </div>
                    <button type="button" onClick={() => excluirCompraCartao(compra.id)} aria-label="Excluir compra"><Trash2 size={14} /></button>
                  </div>
                ))
              )}
            </div>
          </section>
        </div>
      )}

      {modalCompraAberto && cartaoCompraAtual && (
        <div className="debt-modal-backdrop">
          <section className="debt-modal-card">
            <div className="debt-modal-header"><div><h2>Nova compra</h2><p>Cartão: {cartaoCompraAtual.nome}</p></div><button type="button" onClick={() => { setModalCompraAberto(false); setCartaoCompraAtual(null); }}><X size={20} /></button></div>
            <form className="debt-form" onSubmit={cadastrarCompraCartao}>
              <label>Descrição<input value={descricaoCompra} onChange={(e) => setDescricaoCompra(e.target.value)} placeholder="Ex: Mercado" /></label>
              <div className="debt-form-grid"><label>Valor total da compra<input value={valorCompra} onChange={(e) => setValorCompra(e.target.value)} placeholder="30,99" inputMode="decimal" /></label><label>Quantidade de parcelas<input value={parcelasCompra} onChange={(e) => setParcelasCompra(e.target.value)} placeholder="1" inputMode="numeric" /></label></div>
              <div className="debt-form-grid"><label>Data da compra<input type="date" value={dataCompra} onChange={(e) => setDataCompra(e.target.value)} /></label><label>Categoria<input value={categoriaCompra} onChange={(e) => setCategoriaCompra(e.target.value)} placeholder="Ex: Alimentação" /></label></div>
              {moedaParaNumero(valorCompra) > 0 && (Number(parcelasCompra || 1) || 1) > 1 && (
                <div className="debt-installment-preview">
                  <span>Serão geradas {Number(parcelasCompra || 1) || 1} parcelas de aproximadamente</span>
                  <strong>{formatarMoeda(moedaParaNumero(valorCompra) / Math.max(Number(parcelasCompra || 1) || 1, 1))}</strong>
                </div>
              )}
              {erroCompra && <div className="debt-modal-error">{erroCompra}</div>}
              <button className="debt-submit-btn" type="submit">Salvar compra</button>
            </form>
          </section>
        </div>
      )}
    </section>
  );
}

function DebtSummaryCard({ color, title, value, caption, icon }) {
  return (
    <article className={`debt-summary-card ${color}`}>
      <div><span>{title}</span><strong>{value}</strong><p>{caption}</p></div>
      <div className="debt-summary-icon">{icon}</div>
    </article>
  );
}
